import { z } from "zod";
import {
  RECOMMENDATION_ATTRIBUTION_CAPABILITY, RECOMMENDATION_ENVIRONMENTS, RECOMMENDATION_SCHEMA_VERSION,
  RECOMMENDATION_SURFACES, RECOMMENDATION_TEMPORARY_REASONS,
  RECOMMENDATION_TRIGGERS, type RecommendationRequestIdentity,
} from "@batch-and-bowl/shared";

const uuid = z.string().uuid();
// Application IDs include older import IDs and Better Auth's opaque IDs.
// This permits their alphabet, never URLs, whitespace or arbitrary prose.
const opaqueId = z.string().min(1).max(200).regex(/^[A-Za-z0-9_-]+$/);
const mealType = z.enum(["breakfast", "lunch", "dinner", "snack"]);
const scope = z.enum(["global", "breakfast", "lunch", "dinner", "snack"]);
const count = z.number().int().min(0).max(100_000);
const uniqueIds = (minimum = 0, maximum = 100) => z.array(opaqueId).min(minimum).max(maximum)
  .refine(ids => new Set(ids).size === ids.length, "Duplicate identifiers");
const link = { attemptId: uuid, decisionId: uuid };

export const recommendationRequestIdentitySchema = z.object({
  ...link, surface: z.enum(RECOMMENDATION_SURFACES), trigger: z.enum(RECOMMENDATION_TRIGGERS),
}).strict() satisfies z.ZodType<RecommendationRequestIdentity>;

export const recommendationAttributionReceiptSchema = recommendationRequestIdentitySchema.extend({
  capability: z.literal(RECOMMENDATION_ATTRIBUTION_CAPABILITY), schemaVersion: z.literal(RECOMMENDATION_SCHEMA_VERSION),
}).strict();

export const recommendationChoiceContextSchema = z.union([
  z.object({ surface: z.enum(["profile", "unknown"]) }).strict(),
  z.object({ ...link, surface: z.enum(RECOMMENDATION_SURFACES), mealType }).strict(),
]);
export type RecommendationChoiceContext = z.infer<typeof recommendationChoiceContextSchema>;

export const recommendationOutcomeContextSchema = z.object({
  operationId: uuid, mealPlanId: opaqueId, mealSlotId: opaqueId,
}).strict();

export const recommendationAcceptanceIdentitySchema = z.object({
  attemptId: uuid,
  decisionIds: z.array(uuid).min(1).max(28).refine(ids => new Set(ids).size === ids.length, "Duplicate decisions"),
  choices: z.array(z.object({ mealType, recipeId: opaqueId, decisionId: uuid }).strict()).min(1).max(28).optional(),
}).strict().superRefine((input, ctx) => {
  if (input.choices && (new Set(input.choices.map(choice => `${choice.mealType}:${choice.recipeId}`)).size !== input.choices.length || input.choices.some(choice => !input.decisionIds.includes(choice.decisionId)))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid selected decision mapping" });
  }
});

const signal = { operationId: uuid, ...link, mealType };
export const recommendationSignalSchema = z.discriminatedUnion("kind", [
  z.object({ ...signal, kind: z.literal("offered"), recipeIds: uniqueIds(1), freshRecipeIds: uniqueIds(), batchIndex: count }).strict(),
  z.object({ ...signal, kind: z.literal("selection"), recipeId: opaqueId, position: count, selected: z.boolean() }).strict(),
  z.object({ ...signal, kind: z.literal("dismissed"), recipeId: opaqueId, reason: z.enum(RECOMMENDATION_TEMPORARY_REASONS).nullable() }).strict(),
]).superRefine((input, ctx) => {
  if (input.kind === "offered" && input.freshRecipeIds.some(id => !input.recipeIds.includes(id))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Fresh choices must be offered choices" });
  }
});

const envelope = {
  eventId: uuid,
  schemaVersion: z.literal(RECOMMENDATION_SCHEMA_VERSION),
  algorithmVersion: z.string().min(1).max(80).regex(/^[A-Za-z0-9._-]+$/),
  ...link,
  surface: z.enum(RECOMMENDATION_SURFACES),
  environment: z.enum(RECOMMENDATION_ENVIRONMENTS),
  releaseSha: z.string().length(40).regex(/^[a-f0-9]{40}$/).nullable(),
  analyticsCohort: z.enum(["external", "internal", "test", "unknown"]),
};
export const recommendationDecisionEnvelopeSchema = z.object(envelope).omit({ eventId: true }).strict();
export const recommendationPracticalFitEvidenceSchema = z.object({
  version: z.literal('practical-fit-v1'), coverage: z.enum(['full', 'partial', 'none', 'unknown']),
  preparationEvidence: z.enum(['time_only', 'unknown']),
}).strict();
export type RecommendationPracticalFitEvidence = z.infer<typeof recommendationPracticalFitEvidenceSchema>;
const practicalFitCounts = z.object({
  version: z.literal('practical-fit-v1'), basis: z.literal('evaluated_candidates'),
  fullCount: count, partialCount: count, noneCount: count, unknownCount: count, unavailableCount: count,
  timeKnownCount: count, timeUnknownCount: count,
}).strict();
export const recommendationDecisionResultSchema = z.object({
  sections: z.array(z.object({
    mealType, batchIndex: count,
    candidates: z.array(z.object({
      recipeId: opaqueId, version: z.string().length(64).regex(/^[a-f0-9]{64}$/), pantryOverlap: z.boolean(),
      practicalFit: recommendationPracticalFitEvidenceSchema.optional(),
    }).strict()).max(100),
    eligibleCount: count, unseenCount: count, freshCount: count, selectedCount: count, exhausted: z.boolean(),
  }).strict()).max(4),
}).strict().superRefine((result, ctx) => {
  if (new Set(result.sections.map(section => section.mealType)).size !== result.sections.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Duplicate meal sections" });
  }
  for (const section of result.sections) {
    if (new Set(section.candidates.map(candidate => candidate.recipeId)).size !== section.candidates.length ||
      section.candidates.length + section.selectedCount > section.eligibleCount || section.unseenCount + section.selectedCount > section.eligibleCount ||
      section.freshCount > section.candidates.length || section.freshCount > section.unseenCount || section.unseenCount > section.eligibleCount) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory decision section" });
    }
  }
});
export type RecommendationDecisionEnvelope = z.infer<typeof recommendationDecisionEnvelopeSchema>;
export type RecommendationDecisionResult = z.infer<typeof recommendationDecisionResultSchema>;
const outcomeProperties = z.object({
  ...envelope, attemptId: uuid.nullable(), decisionId: uuid.nullable(), surface: z.enum([...RECOMMENDATION_SURFACES, "unknown"]),
  operationId: uuid, mealPlanId: opaqueId, mealSlotId: opaqueId, recipeId: opaqueId, mealType, groceryListId: opaqueId.nullable(),
  linkageStatus: z.enum(["linked", "unknown_origin", "slot_changed", "recipe_changed", "future_slot"]),
  acceptanceOperationId: uuid.nullable(), acceptanceAgeDays: count.nullable(),
}).strict();
function validateOutcomeLink(p: z.infer<typeof outcomeProperties>, ctx: z.RefinementCtx) {
  const linked = p.linkageStatus === "linked";
  if (linked ? p.attemptId === null || p.decisionId === null || p.surface === "unknown" || p.acceptanceOperationId === null || p.acceptanceAgeDays === null
    : p.attemptId !== null || p.decisionId !== null || p.surface !== "unknown" || p.acceptanceOperationId !== null || p.acceptanceAgeDays !== null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory outcome linkage" });
  }
}
const propertySchemas = {
  meal_cooked: outcomeProperties.superRefine(validateOutcomeLink),
  meal_rated: outcomeProperties.extend({ value: z.enum(["up", "down"]) }).strict().superRefine(validateOutcomeLink),
  plan_preview_generated: z.object({ ...envelope, mode: z.enum(["create", "fill", "replace"]), enabledMealTypeCount: z.number().int().min(0).max(4), targetCount: count, returnedCount: count }).strict(),
  plan_preview_refreshed: z.object({ ...envelope, mealType, selectedCount: count, freshCount: count, exhausted: z.boolean() }).strict(),
  server_recommendation_requested: z.object({ ...envelope, trigger: z.enum(RECOMMENDATION_TRIGGERS) }).strict(),
  server_recommendation_decision: z.object({
    ...envelope, state: z.enum(["served", "no_result"]), candidateCount: count,
    eligibleCount: count, unseenCount: count, freshCount: count, selectedCount: count,
    sectionCount: z.number().int().min(0).max(4), exhausted: z.boolean(),
    practicalFit: practicalFitCounts.optional(),
  }).strict().superRefine((p, ctx) => {
    if (p.candidateCount + p.selectedCount > p.eligibleCount || p.unseenCount + p.selectedCount > p.eligibleCount ||
      p.freshCount > p.candidateCount || p.freshCount > p.unseenCount || p.unseenCount > p.eligibleCount ||
      (p.sectionCount === 0 && (p.candidateCount !== 0 || p.eligibleCount !== 0 || p.selectedCount !== 0)) ||
      (p.state === "no_result") !== (p.candidateCount === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory decision counts" });
    }
    if (p.practicalFit && (p.practicalFit.fullCount + p.practicalFit.partialCount + p.practicalFit.noneCount + p.practicalFit.unknownCount + p.practicalFit.unavailableCount !== p.candidateCount
      || p.practicalFit.timeKnownCount + p.practicalFit.timeUnknownCount + p.practicalFit.unavailableCount !== p.candidateCount)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Practical-fit counts must use the evaluated candidate denominator' });
    }
  }),
  server_recommendation_failed: z.object({
    ...envelope, reason: z.enum(["conflict", "evaluation_failed", "unavailable"]),
  }).strict(),
  plan_recommendation_batch_shown: z.object({
    ...envelope, mealType, batchIndex: count, recipeIds: uniqueIds(1),
    eligibleCount: count, unseenCount: count, freshCount: count,
    pantryMatchCount: count, selectedCount: count, exhausted: z.boolean(),
  }).strict().superRefine((p, ctx) => {
    if (p.freshCount > p.recipeIds.length || p.pantryMatchCount > p.recipeIds.length || p.unseenCount > p.eligibleCount) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory offered counts" });
    }
  }),
  plan_recommendation_selection_changed: z.object({
    ...envelope, operationId: uuid, mealType, recipeId: opaqueId,
    position: count, selected: z.boolean(), pantryOverlap: z.boolean(),
  }).strict(),
  recommendation_dismissed: z.object({
    ...envelope, operationId: uuid, mealType, recipeId: opaqueId,
    reason: z.enum(RECOMMENDATION_TEMPORARY_REASONS).nullable(),
  }).strict(),
  server_recommendation_choice_saved: z.object({
    ...envelope, attemptId: uuid.nullable(), decisionId: uuid.nullable(),
    surface: z.enum([...RECOMMENDATION_SURFACES, "profile", "unknown"]), operationId: uuid,
    action: z.enum(["suppress", "restore", "reset"]),
    reason: z.enum(["not_for_meal_type", "dont_suggest"]).nullable(), scope: scope.nullable(), recipeId: opaqueId.nullable(),
  }).strict().superRefine((p, ctx) => {
    if ((p.attemptId === null) !== (p.decisionId === null) ||
      (["profile", "unknown"].includes(p.surface)) !== (p.attemptId === null) ||
      (p.action === "reset" ? p.scope !== null || p.recipeId !== null || p.reason !== null : p.scope === null || p.recipeId === null) ||
      (p.action === "suppress" && p.reason !== (p.scope === "global" ? "dont_suggest" : "not_for_meal_type")) ||
      (p.action === "restore" && p.reason !== null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory saved choice" });
    }
  }),
  plan_recommendation_committed: z.object({
    ...envelope, decisionId: uuid.nullable(),
    decisionIds: z.array(uuid).min(1).max(28).refine(ids => new Set(ids).size === ids.length, "Duplicate decisions"),
    operationId: uuid, mealPlanId: opaqueId, groceryListId: opaqueId.nullable(),
    slotIds: uniqueIds(1, 100), recipeIds: z.array(opaqueId).min(1).max(100),
    selectedCount: count, openSlotCount: count,
  }).strict().superRefine((p, ctx) => {
    if (p.selectedCount !== p.slotIds.length || p.recipeIds.length !== p.slotIds.length ||
      (p.decisionId !== null && !p.decisionIds.includes(p.decisionId))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Contradictory accepted slots" });
    }
  }),
};

export const recommendationEventSchema = z.discriminatedUnion("event", [
  z.object({ event: z.literal("meal_cooked"), properties: propertySchemas.meal_cooked }).strict(),
  z.object({ event: z.literal("meal_rated"), properties: propertySchemas.meal_rated }).strict(),
  z.object({ event: z.literal("plan_preview_generated"), properties: propertySchemas.plan_preview_generated }).strict(),
  z.object({ event: z.literal("plan_preview_refreshed"), properties: propertySchemas.plan_preview_refreshed }).strict(),
  z.object({ event: z.literal("server_recommendation_requested"), properties: propertySchemas.server_recommendation_requested }).strict(),
  z.object({ event: z.literal("server_recommendation_decision"), properties: propertySchemas.server_recommendation_decision }).strict(),
  z.object({ event: z.literal("server_recommendation_failed"), properties: propertySchemas.server_recommendation_failed }).strict(),
  z.object({ event: z.literal("plan_recommendation_batch_shown"), properties: propertySchemas.plan_recommendation_batch_shown }).strict(),
  z.object({ event: z.literal("plan_recommendation_selection_changed"), properties: propertySchemas.plan_recommendation_selection_changed }).strict(),
  z.object({ event: z.literal("recommendation_dismissed"), properties: propertySchemas.recommendation_dismissed }).strict(),
  z.object({ event: z.literal("server_recommendation_choice_saved"), properties: propertySchemas.server_recommendation_choice_saved }).strict(),
  z.object({ event: z.literal("plan_recommendation_committed"), properties: propertySchemas.plan_recommendation_committed }).strict(),
]);

export type RecommendationSignal = z.infer<typeof recommendationSignalSchema>;
export type RecommendationEvent = z.infer<typeof recommendationEventSchema>;
export type RecommendationAcceptanceIdentity = z.infer<typeof recommendationAcceptanceIdentitySchema>;
